import React, { useRef, useState, useEffect } from "react";
import { Box, Paper } from "@material-ui/core";
import { ScheduleGroupCode, ScheduleCountrySelect } from "./children";
import { Sheet, SheetContent } from "../../components";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useLazyQuery } from "@apollo/react-hooks";
import {
  GGV_GET_SCREEN_FLOW_SEQUENCE,
  GET_SCREEN_FLOW_SEQUENCE as GGT_GET_SCREEN_FLOW_SEQUENCE
} from "../../queries";
import {
  GROUP_CODE_DEFAULT,
  ROUTE_PATHS,
  BRAND_GGT,
  BRAND_GGV,
  CONSUMER_SESSION_VERIFICATION_TOKEN,
  COUNTRIES_ISO_CODES
} from "../../constants";

const ScheduleScreener = ({ brand = BRAND_GGT }) => {
  const history = useHistory();
  const location = useLocation();
  const { group_code = "" } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [drawerClearance, setDrawerClearance] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState();
  const groupCode = useRef(null);

  /**
   * Get correct screen flow query depending on brand
   *
   * @param {string} brand
   */
  const getScreenFlowQuery = brand => {
    switch (brand) {
      case BRAND_GGV:
        return GGV_GET_SCREEN_FLOW_SEQUENCE;
      default:
        return GGT_GET_SCREEN_FLOW_SEQUENCE;
    }
  };

  /**
   * Get screen flow from API
   */
  const [getScreenFlowSequence, { loading }] = useLazyQuery(
    getScreenFlowQuery(brand),
    {
      onCompleted: ({ screenFlowSequence: seq }) => {
        if (seq && seq.screens && seq.screens.length > 0) {
          const { search } = location;

          const path =
            brand === BRAND_GGV
              ? ROUTE_PATHS.GGV.SCHEDULE_BASE
              : ROUTE_PATHS.GGT.SCHEDULE_BASE;

          history.push({
            pathname: path,
            search,
            state: {
              country: selectedCountry,
              screens: seq.screens,
              screenConfig: seq.config,
              groupCode: groupCode.current || GROUP_CODE_DEFAULT,
              verification_token:
                sessionStorage.getItem(CONSUMER_SESSION_VERIFICATION_TOKEN) ||
                ""
            }
          });
        } else {
          enqueueSnackbar("Invalid group code", { variant: "error" });
        }
      },
      fetchPolicy: "no-cache"
    }
  );

  const handleSubmit = patientGroupCode => {
    groupCode.current = patientGroupCode;

    getScreenFlowSequence({ variables: { groupCode: patientGroupCode } });
  };

  /**
   * Determines whether we need a country selection
   *
   * @returns {boolean}
   */
  const requiresCountrySelection = () =>
    brand === BRAND_GGT && !selectedCountry && !group_code;

  /**
   * If we have a group code in the url
   */
  useEffect(() => {
    if (!group_code || requiresCountrySelection()) return;

    handleSubmit(group_code);
  }, [selectedCountry, group_code]);

  return (
    <>
      <Sheet
        data-cy={`${brand}-schedule-screener-view`}
        style={{ paddingBottom: `${drawerClearance}px` }}
      >
        <SheetContent>
          <Box mb={2}>
            {requiresCountrySelection() ? (
              <ScheduleCountrySelect setSelectedCountry={setSelectedCountry} />
            ) : !group_code ? (
              <Paper>
                <Box p={2}>
                  <ScheduleGroupCode
                    setDrawerClearance={setDrawerClearance}
                    handleSubmit={handleSubmit}
                    loading={loading}
                    countryCode={selectedCountry || COUNTRIES_ISO_CODES.US}
                  />
                </Box>
              </Paper>
            ) : null}
          </Box>
        </SheetContent>
      </Sheet>
    </>
  );
};

export { ScheduleScreener };
